<script setup>
const emit = defineEmits(['change'])
const props = defineProps({
  min: {
    type: Number,
    default: 0
  },
  max: {
    type: Number,
    default: 60
  }
})

const thumbs = defineModel({ type: Array })
const range = computed(() => thumbs.value.map(x => Math.round(x)))
const popovers = ref(thumbs.value.map(() => false))

const slider = ref(null)
const isDragging = ref(false)
const activeThumbIndex = ref(null)
const sliderWidth = ref(0)
const isVisible = useElementVisibility(slider)

const onMouseDown = (event) => {
  const rect = slider.value.getBoundingClientRect()
  const closestThumbIndex = getClosestThumbIndex(event.clientX - rect.left)
  setActiveThumb(closestThumbIndex)
  onStartDrag(event)
}

const onThumbTouchStart = (event, index) => {
  setActiveThumb(index)
  onStartDrag(event.touches[0])
}

const onTouchStart = (event) => {
  const rect = slider.value.getBoundingClientRect()
  const closestThumbIndex = getClosestThumbIndex(event.touches[0].clientX - rect.left)
  setActiveThumb(closestThumbIndex)
  onStartDrag(event.touches[0])
}

const onThumbMouseDown = (event, index) => {
  setActiveThumb(index)
  onStartDrag(event)
}

const setActiveThumb = (index) => {
  activeThumbIndex.value = index
  popovers.value[index] = true
}

const onStartDrag = (event) => {
  const rect = slider.value.getBoundingClientRect()
  isDragging.value = true
  updateThumbPosition(event.clientX - rect.left)
}

const onMouseUp = () => {
  const wasDragging = isDragging.value
  isDragging.value = false

  popovers.value[activeThumbIndex.value] = false
  activeThumbIndex.value = null
  if (wasDragging) emit('change', range.value)
}

const onMouseMove = (event) => {
  if (!isDragging.value || activeThumbIndex.value === null) return
  const rect = slider.value.getBoundingClientRect()
  updateThumbPosition(event.clientX - rect.left)
}

const updateThumbPosition = (position) => {
  const percentage = getPercentage(position)

  if (activeThumbIndex.value === 0) {
    thumbs.value[0] = Math.max(props.min, Math.min(thumbs.value[1], percentage))
  } else if (activeThumbIndex.value === 1) {
    thumbs.value[1] = Math.max(thumbs.value[0], Math.min(props.max, percentage))
  }
}

const getClosestThumbIndex = (position) => {
  const percentage = getPercentage(position)

  return Math.abs(thumbs.value[0] - percentage) < Math.abs(thumbs.value[1] - percentage) ? 0 : 1
}

const getPercentage = (position) => ((position / sliderWidth.value) * 100 / 100) * props.max

// prevent thumb being stuck in dragging state
// when dragging outside of slider
useEventListener(document, 'mouseup', onMouseUp)
useEventListener(document, 'mousemove', onMouseMove)
useEventListener(document, 'touchend', onMouseUp)
useEventListener(document, 'touchmove', onMouseMove)

watch(isVisible, () => sliderWidth.value = slider.value.clientWidth)
</script>

<template>
  <div 
    ref="slider" 
    class="relative w-[calc(100%_-_20px)] mx-auto h-[3px] bg-gray-300 rounded-md select-none cursor-grab"
    :class="{ 'cursor-grabbing': isDragging }"
    @mousedown.self="onMouseDown" 
    @touchstart.self.passive="onTouchStart"
  >
    <div
      class="absolute top-0 left-0 h-full bg-blue-500 cursor-grab"
      :class="{ 'cursor-grabbing': isDragging }"
      :style="{
        left: `${(thumbs[0] / props.max) * 100}%`,
        width: `${((thumbs[1] - thumbs[0]) / props.max) * 100}%`,
      }"
      @mousedown.self="onMouseDown" 
      @touchstart.self.passive="onTouchStart"
    />

    <div
      v-for="(thumb, index) in thumbs"
      :key="index" 
      :class="{ 'cursor-grabbing': isDragging }"
      :style="{ left: `${(thumb / props.max) * 100}%` }"
      class="absolute top-0"
    >
      <div class="relative">
        <div
          class="absolute size-4 -mt-1.5 bg-blue-500 rounded-full cursor-grab -translate-x-1/2 ring-2 ring-blue-500"
          @mousedown.self="onThumbMouseDown($event, index)" 
          @touchstart.self.passive="onThumbTouchStart($event, index)"
        />
        <Transition name="fade" duration="300">
          <div v-if="activeThumbIndex == index" class="absolute left-0 -mt-[3.5em] translate-x-[-50%]">
            <div class="min-w-[3em] aspect-square shadow-sm p-2.5 bg-gray-100 rounded-xl flex items-center justify-center text-xs">
              {{ range[activeThumbIndex] }}
            </div>
          </div>
        </Transition>
      </div>
    </div>

  </div>
</template>
